/**
 * The review cards' sort menu (sections/reviews-paper.liquid,
 * assets/reviews-row.css), in both eras.
 *
 * Judge.me sorts its reviews with a native <select>, which opens the
 * system's own menu. The theme's other selectors (the header's currency,
 * language and era) are a popover-button that opens a <popover-content>, a
 * list of choices on desktop and a bottom sheet on a phone, so the sort
 * menu is one too: built from the section's template, one choice for each
 * of the select's options. The select stays in the widget, hidden, as the
 * source of truth: choosing a choice sets its value and dispatches the
 * change event Judge.me listens for, so it sorts as it always did. The
 * popover brings the theme's own keyboard and screen reader behaviour (focus
 * held in it while it is open, Escape to close, the button's expanded state).
 *
 * The widget is the app's own markup and re-renders as it pages, sorts and
 * switches tabs, so the menu is rebuilt, or just brought up to date, from
 * the select as it now is, as the widget changes (one observer, on the
 * widget alone, that lets go when the widget leaves the page). Fails
 * quietly: nothing here throws into the page.
 */

(() => {
  const ROOT_ID = "judgeme_product_reviews";
  const template = document.getElementById("reviews-sort-template");
  if (!template || document.documentElement.hasAttribute("data-reviews-sort"))
    return;
  document.documentElement.setAttribute("data-reviews-sort", "");

  /* The menus on the page, by the widget's filter each one sits in. */
  const menus = new Map();
  let count = 0;

  const optionsOf = (select) =>
    [...select.options].map((option) => ({
      value: option.value,
      label: option.textContent.trim()
    }));

  const choiceOf = ({ value, label }) => {
    const item = document.createElement("button");
    const text = document.createElement("span");
    item.type = "button";
    item.className = "popover__choice-item";
    item.dataset.value = value;
    text.className = "popover__choice-label";
    text.textContent = label;
    item.append(text);
    return item;
  };

  /* The menu as the select now is: its choices, the one chosen, and the
     value on the button. Writes nothing that is already so, as the widget's
     observer would see it and come round again. */
  const syncMenu = (menu) => {
    const { select, list, value } = menu;
    const options = optionsOf(select);
    const signature = JSON.stringify(options);
    if (signature !== menu.signature) {
      menu.signature = signature;
      list.replaceChildren(...options.map(choiceOf));
    }
    const chosen = select.selectedOptions[0]?.textContent.trim() ?? "";
    if (value.textContent !== chosen) value.textContent = chosen;
    list.querySelectorAll(".popover__choice-label").forEach((label) => {
      const current = label.parentElement.dataset.value === select.value;
      if (label.hasAttribute("aria-current") === current) return;
      if (current) label.setAttribute("aria-current", "true");
      else label.removeAttribute("aria-current");
    });
  };

  /* Where the popover is the theme's popover works: beside its button on a
     wide screen, where it hangs below it, and on the page's body on a
     narrow one, where it is a sheet fixed to the screen's foot. The
     widget's boxes are containers (container-type), which hold a fixed
     box to themselves, so there it can't stay under the button. Moved
     closed, as it would otherwise lose its focus. */
  const sheet = window.matchMedia("(max-width: 999px)");

  const place = (menu) => {
    const home = sheet.matches ? document.body : menu.container;
    if (menu.popover.parentElement === home) return;
    menu.popover.removeAttribute("open");
    home.append(menu.popover);
  };

  const choose = (menu, value) => {
    const { select, popover } = menu;
    popover.removeAttribute("open");
    if (select.value === value) return;
    select.value = value;
    select.dispatchEvent(new Event("change", { bubbles: true }));
    syncMenu(menu);
  };

  /* The select under the menu: out of the way and of the tab order and
     the accessibility tree, and the menu told when something else changes
     it. */
  const adopt = (menu, select) => {
    menu.select?.removeEventListener("change", menu.onChange);
    menu.select = select;
    select.addEventListener("change", menu.onChange);
    select.setAttribute("aria-hidden", "true");
    select.tabIndex = -1;
  };

  const buildMenu = (filter, select) => {
    const container = document.importNode(
      template.content,
      true
    ).firstElementChild;
    const popover = container.querySelector("popover-content");
    const button = container.querySelector(".popover-button");
    const title = filter.querySelector("label")?.textContent.trim();
    const id = `reviews-sort-${++count}`;
    popover.id = id;
    button.setAttribute("aria-controls", id);
    if (title) {
      container.querySelector("[data-sort-title]").textContent = title;
      container.querySelector("[data-sort-label]").textContent = title;
    }
    const menu = {
      filter,
      container,
      popover,
      list: container.querySelector("[data-sort-choices]"),
      value: container.querySelector("[data-sort-value]"),
      select: null,
      signature: "",
      onChange: () => syncMenu(menu)
    };
    menu.list.addEventListener("click", (event) => {
      const item = event.target.closest(".popover__choice-item");
      if (item) choose(menu, item.dataset.value);
    });
    adopt(menu, select);
    syncMenu(menu);
    filter.append(container);
    place(menu);
    return menu;
  };

  /* A menu whose filter the widget has dropped. An open popover locks the
     page's scroll on a phone (lock-mobile on the root), and takes it off as
     it closes, so it is closed first. */
  const dropMenu = (menu) => {
    menu.popover.removeAttribute("open");
    menu.popover.remove();
    menu.select?.removeEventListener("change", menu.onChange);
    menus.delete(menu.filter);
  };

  const refresh = (root) => {
    menus.forEach((menu) => {
      if (!menu.container.isConnected) dropMenu(menu);
    });
    root.querySelectorAll(".jm-sort-filter").forEach((filter) => {
      const select = filter.querySelector("select");
      const menu = menus.get(filter);
      if (!select) return;
      if (!menu) {
        menus.set(filter, buildMenu(filter, select));
        return;
      }
      if (menu.select !== select) adopt(menu, select);
      place(menu);
      syncMenu(menu);
    });
  };

  const watch = () => {
    const root = document.getElementById(ROOT_ID);
    if (!root) return;
    let frame = 0;
    const replace = () => menus.forEach(place);
    const stop = () => {
      watching.disconnect();
      cancelAnimationFrame(frame);
      sheet.removeEventListener("change", replace);
      menus.forEach(dropMenu);
      window.removeEventListener("pagehide", leave);
    };
    /* A page kept for the back button (bfcache) comes back as it was left,
       so its menus are only closed (an open one would come back holding the
       phone's scroll lock); a page let go of lets go of them. */
    const leave = (event) => {
      if (!event.persisted) return stop();
      menus.forEach((menu) => menu.popover.removeAttribute("open"));
    };
    const watching = new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!root.isConnected) return stop();
        try {
          refresh(root);
        } catch (error) {
          console.error("reviews-sort:", error);
        }
      });
    });
    try {
      refresh(root);
    } catch (error) {
      console.error("reviews-sort:", error);
    }
    watching.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["value"]
    });
    sheet.addEventListener("change", replace);
    window.addEventListener("pagehide", leave);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", watch, { once: true });
  } else {
    watch();
  }
})();
